import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SortFunction } from "~/lib/sort";
import style from "./index.module.scss";
import { IColumn, ITableProps, Table } from "./Table";
import { Pagination } from "../Pagination";

export enum TableSortDirection {
  ASC = 1,
  DESC = -1,
}

export interface ISortColumn<T> extends IColumn<T> {
  sort?: SortFunction<T>;
  backendSort?: boolean;
}

export interface ITableSortProps<T> extends ITableProps<T> {
  columns: ISortColumn<T>[];
  defaultSort?: ITableSortDefault<T>;
  maxItems?: number;
  withPagination?: boolean;
  onSortChange?: (args: { field: keyof T, direction: TableSortDirection, backendSort?: boolean }) => void;
}

export interface ITableSortDefault<T> {
  sort: SortFunction<T>;
  field: keyof T;
  direction: TableSortDirection;
}

export interface ITableSortState<T> {
  sort: SortFunction<T>;
  sortFieldName: keyof T;
  sortDirection: TableSortDirection;
}

export function TableSort<T>(props: ITableSortProps<T>) {
  const [state, setState] = useState<ITableSortState<T>>(
    props.defaultSort
      ? {
        sort: props.defaultSort.sort,
        sortFieldName: props.defaultSort.field,
        sortDirection: props.defaultSort.direction,
      }
      : {
        sort: null,
        sortFieldName: null,
        sortDirection: null,
      }
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { sort, sortDirection, sortFieldName } = state;
  const { records, columns } = props;
  const sortedRecords = useMemo(
    () => (sort ? sort([...records], sortFieldName as string, sortDirection) : records),
    [records, sort, sortFieldName, sortDirection]
  );
  const backendSortingActive =
    columns.find(col => col.field === sortFieldName && (col as ISortColumn<T>).backendSort);
  const queryParams = new URLSearchParams(location.search);
  const pageSize = queryParams.get("pageSize") ? parseInt(queryParams.get("pageSize")!, 10) : 25;
  const page = queryParams.get("page") ? parseInt(queryParams.get("page")!, 10) : 0;
  const paginatedRecords = useMemo(
    () => (props.withPagination ? sortedRecords.slice(page * pageSize, (page + 1) * pageSize) : sortedRecords),
    [sortedRecords, page, pageSize]
  );

  const setQueryParams = (params: Record<string, string | null>) => {
    const newQueryParams = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === "") {
        newQueryParams.delete(key);
        continue;
      }
      newQueryParams.set(key, value);
    }
    navigate({ search: newQueryParams.toString() }, { replace: true });
  };

  useEffect(() => {
    if (page * pageSize >= sortedRecords.length && sortedRecords.length > 0) {
      setQueryParams({
        page: "0",
      });
    }
  }, [sortedRecords.length]);

  return (
    <>
      <Table
        {...props}
        columns={columns.map<IColumn<T>>((columnSort) => {
          let { sort: sortFn, ...column } = columnSort;
          let { headerAttrs = {} } = column;
          let classNames = [headerAttrs.className];
          if (sortFn) {
            classNames.push(style.tableHeadSort);
          }
          if (column.field === sortFieldName) {
            if (sortDirection === TableSortDirection.DESC) {
              classNames.push(style.tableSortDesc);
            } else if (sortDirection === TableSortDirection.ASC) {
              classNames.push(style.tableSortAsc);
            }
          }
          column.headerAttrs = {
            ...headerAttrs,
            onClick: () => {
              let direction = sortDirection;
              if (sortFieldName === column.field) {
                direction = direction === TableSortDirection.ASC ? TableSortDirection.DESC : TableSortDirection.ASC;
              } else {
                direction = TableSortDirection.ASC;
              }

              if ((columnSort as ISortColumn<T>).backendSort && typeof props.onSortChange === 'function') {
                props.onSortChange({ field: column.field, direction, backendSort: true });
                setState({
                  sort: null,
                  sortDirection: direction,
                  sortFieldName: column.field,
                });
                return;
              }

              if (sortFn) {
                setState({
                  sort: sortFn,
                  sortDirection: direction,
                  sortFieldName: column.field,
                });
              }
            },
            className: classNames.join(" "),
          };
          return column;
        })}
        records={props.maxItems
          ? (backendSortingActive ? records.slice(0, props.maxItems) : paginatedRecords.slice(0, props.maxItems))
          : (backendSortingActive ? records : paginatedRecords)}
      />
      {props.withPagination && (
        <Pagination
          padding={4}
          offset={pageSize * page}
          limit={pageSize}
          total={sortedRecords.length}
          onChange={(page, perPage) => {
            setQueryParams({
              page: page.toString(),
              pageSize: perPage.toString(),
            });
          }}
        />
      )}
    </>
  );
}
